import { type Transition, transitionGuard } from './machine';

/**
 * Case lifecycle (PRD §6). `payment_captured` belongs to Phase 3; Phase 1 closes
 * straight from `session_complete` (PST-07), and Phase 3 inserts the payment step.
 */
export const CASE_STATES = [
  'draft',
  'submitted',
  'on_hold',
  'released',
  'scheduled',
  'session_complete',
  'payment_captured',
  'closed',
  'canceled',
] as const;
export type CaseState = (typeof CASE_STATES)[number];

export type CaseEvent =
  | 'submit'
  | 'hold'
  | 'clear_hold'
  | 'release'
  | 'schedule'
  | 'unschedule'
  | 'complete_session'
  | 'close'
  | 'cancel';

const PRE_CLOSURE: readonly CaseState[] = ['draft', 'submitted', 'on_hold', 'released', 'scheduled'];

const TRANSITIONS: readonly Transition<CaseState, CaseEvent>[] = [
  { from: ['draft'], on: 'submit', to: 'submitted' },
  { from: ['submitted'], on: 'hold', to: 'on_hold' },
  { from: ['on_hold'], on: 'clear_hold', to: 'submitted' },
  { from: ['submitted'], on: 'release', to: 'released' },
  { from: ['released'], on: 'schedule', to: 'scheduled' },
  { from: ['scheduled'], on: 'unschedule', to: 'released' },
  { from: ['scheduled'], on: 'complete_session', to: 'session_complete' },
  { from: ['session_complete'], on: 'close', to: 'closed' },
  { from: PRE_CLOSURE, on: 'cancel', to: 'canceled' },
];

export const caseMachine = transitionGuard<CaseState, CaseEvent>('case', TRANSITIONS);

/** Closed and canceled cases are read-only (PST-07). */
export function isCaseReadOnly(state: CaseState): boolean {
  return state === 'closed' || state === 'canceled';
}

/** Navigator-facing labels, matching the prototype pills. */
export const CASE_STATE_LABEL: Record<CaseState, string> = {
  draft: 'Intake in progress',
  submitted: 'Awaiting release',
  on_hold: 'On hold',
  released: 'Awaiting panel',
  scheduled: 'Scheduled',
  session_complete: 'Awaiting closure',
  payment_captured: 'Payment captured',
  closed: 'Closed',
  canceled: 'Canceled',
};

export type PillTone = 'hold' | 'wait' | 'ok' | 'bad' | 'idle' | 'acc' | 'mins';

export const CASE_STATE_TONE: Record<CaseState, PillTone> = {
  draft: 'idle',
  submitted: 'hold',
  on_hold: 'hold',
  released: 'wait',
  scheduled: 'ok',
  session_complete: 'idle',
  payment_captured: 'idle',
  closed: 'idle',
  canceled: 'bad',
};
